import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ProductImage } from "@/components/inst/ProductImage";
import { Loader2, Plus, Trash2, Upload, Save, X, Eye, EyeOff, ArrowUp, ArrowDown, FileSpreadsheet } from "lucide-react";
import { ImportProductModal } from "@/components/inst/ImportProductModal";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const inp = "w-full px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent";

export function InstProducts() {
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);

  const load = async (keep) => {
    const { data } = await api.get("/inst/products");
    setProducts(data.products || []);
    setSel(keep || data.products?.[0]?.id || null);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = window.prompt("New product name (e.g. F-2001 Magnetic Flow Meter)");
    if (!name) return;
    try { const { data } = await api.post("/inst/products", { name }); load(data.product.id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const current = products.find((p) => p.id === sel);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
      <div className="space-y-2" data-testid="inst-products-list">
        {products.map((p) => (
          <button key={p.id} onClick={() => setSel(p.id)} data-testid={`inst-product-${p.id}`}
            className={`w-full text-left p-3 border transition-colors ${sel === p.id ? "border-[#D4AF37] bg-white" : "border-zinc-200 bg-white hover:border-zinc-400"}`}>
            <div className="text-sm font-medium text-[#0F172A] flex items-center gap-2">{p.name}{!p.active && <EyeOff className="h-3.5 w-3.5 text-zinc-400" />}</div>
            <div className="text-xs text-zinc-400">{p.fields.length} fields</div>
          </button>
        ))}
        <button onClick={add} data-testid="inst-add-product-btn" className="w-full flex items-center justify-center gap-2 border border-dashed border-zinc-400 py-3 text-sm text-zinc-600 hover:border-[#0F172A]"><Plus className="h-4 w-4" /> Add product</button>
        <button onClick={() => setImportOpen(true)} data-testid="inst-import-product-btn" className="w-full flex items-center justify-center gap-2 border border-[#0F172A] bg-[#0F172A] py-3 text-sm text-white hover:bg-[#1e293b]"><FileSpreadsheet className="h-4 w-4 text-[#D4AF37]" /> Import spreadsheet</button>
      </div>
      {importOpen && <ImportProductModal products={products} current={current} onClose={() => setImportOpen(false)} onDone={(id) => { setImportOpen(false); setProducts([]); setSel(null); load(id); }} />}
      <div className="lg:col-span-3">{current && <ProductEditor key={current.id} product={current} onSaved={() => load(current.id)} onDeleted={() => load()} />}</div>
    </div>
  );
}

function ProductEditor({ product, onSaved, onDeleted }) {
  const [f, setF] = useState(() => ({
    ...product, highlightsText: (product.highlights || []).join("\n"),
    fields: product.fields.map((fl) => ({ ...fl, _k: uid(), options: fl.options.map((o) => ({ ...o, _k: uid() })) })),
  }));
  const [saving, setSaving] = useState(false);
  const [imgV, setImgV] = useState(0);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setField = (i, patch) => setF({ ...f, fields: f.fields.map((x, j) => (j === i ? { ...x, ...patch } : x)) });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/inst/products/${product.id}`, {
        name: f.name, code: f.code, description: f.description, highlights_title: f.highlights_title, active: f.active, code_separator: f.code_separator ?? "-",
        highlights: f.highlightsText.split("\n"), fields: f.fields,
      });
      toast.success("Product saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/inst/products/${product.id}/image`, fd); toast.success("Image uploaded"); setF((x) => ({ ...x, has_image: true })); setImgV((v) => v + 1); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };
  const del = async () => {
    if (!window.confirm(`Delete ${product.name}?`)) return;
    try { await api.delete(`/inst/products/${product.id}`); toast.success("Product deleted"); onDeleted(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5 space-y-5" data-testid="inst-product-editor">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <ProductImage product={f} version={imgV} className="aspect-square w-full" testid="inst-product-editor-image" />
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="inst-product-image-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="inst-product-image-upload" className="mt-2 w-full flex items-center justify-center gap-2 border border-zinc-300 py-2 text-xs hover:bg-zinc-50"><Upload className="h-3.5 w-3.5" /> Upload image</button>
        </div>
        <div className="md:col-span-2 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-2 text-xs text-zinc-500">Product name<input value={f.name} onChange={set("name")} data-testid="inst-product-name" className={`${inp} mt-1`} /></label>
            <label className="text-xs text-zinc-500">Code<input value={f.code || ""} onChange={set("code")} data-testid="inst-product-code" className={`${inp} mt-1`} /></label>
          </div>
          <label className="block text-xs text-zinc-500">Model number separator <span className="text-zinc-400">(between codes, e.g. "-"; leave blank to join directly)</span>
            <input value={f.code_separator ?? "-"} onChange={set("code_separator")} maxLength={3} data-testid="inst-product-separator" className={`${inp} mt-1 !w-24 font-mono`} />
          </label>
          <label className="block text-xs text-zinc-500">Description<textarea rows={5} value={f.description || ""} onChange={set("description")} data-testid="inst-product-description" className={`${inp} mt-1`} /></label>
          <label className="block text-xs text-zinc-500">Highlights heading<input value={f.highlights_title || ""} onChange={set("highlights_title")} data-testid="inst-product-highlights-title" className={`${inp} mt-1`} /></label>
          <label className="block text-xs text-zinc-500">Highlights (one per line)<textarea rows={4} value={f.highlightsText} onChange={set("highlightsText")} data-testid="inst-product-highlights" className={`${inp} mt-1`} /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="inst-product-active" /> {f.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} Visible to customers</label>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-[#0F172A]">Model form fields</h3>
          <button onClick={() => setF({ ...f, fields: [...f.fields, { label: "", options: [], _k: uid() }] })} data-testid="inst-add-field-btn" className="flex items-center gap-1 text-xs text-[#0F172A] hover:text-[#B8860B]"><Plus className="h-3.5 w-3.5" /> Add field</button>
        </div>
        <div className="space-y-3" data-testid="inst-fields-list">
          {f.fields.map((fld, i) => <FieldRow key={fld._k} i={i} fld={fld} onChange={(p) => setField(i, p)} onRemove={() => setF({ ...f, fields: f.fields.filter((_, j) => j !== i) })} />)}
        </div>
      </div>
      <div className="flex justify-between gap-3 pt-2 border-t border-zinc-100">
        <button onClick={del} data-testid="inst-product-delete" className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /> Delete product</button>
        <button onClick={save} disabled={saving} data-testid="inst-product-save" className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm hover:bg-[#1E293B] disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save product</button>
      </div>
    </div>
  );
}

function FieldRow({ i, fld, onChange, onRemove }) {
  const [draft, setDraft] = useState("");
  const [code, setCode] = useState("");
  const addOpt = () => {
    const v = draft.trim();
    if (v && !fld.options.some((o) => o.label === v)) onChange({ options: [...fld.options, { label: v, code: code.trim(), _k: uid() }] });
    setDraft(""); setCode("");
  };
  const setOpt = (j, patch) => onChange({ options: fld.options.map((o, k) => (k === j ? { ...o, ...patch } : o)) });
  const move = (j, d) => { const a = [...fld.options]; const t = j + d; if (t < 0 || t >= a.length) return; [a[j], a[t]] = [a[t], a[j]]; onChange({ options: a }); };
  return (
    <div className="border border-zinc-200 p-3" data-testid={`inst-field-${i}`}>
      <div className="flex gap-2">
        <span className="font-mono text-xs text-zinc-400 pt-2.5 w-5">{i + 1}.</span>
        <input value={fld.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Field name" data-testid={`inst-field-label-${i}`} className={`${inp} font-medium`} />
        <button onClick={onRemove} title="Remove field" data-testid={`inst-field-remove-${i}`} className="px-2 text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 space-y-1 pl-7">
        {fld.options.map((o, j) => (
          <div key={o._k} className="flex items-center gap-1.5" data-testid={`inst-field-${i}-option-${j}`}>
            <input value={o.code} onChange={(e) => setOpt(j, { code: e.target.value })} placeholder="Code" className="w-20 px-2 py-1 text-xs font-mono border border-zinc-200" data-testid={`inst-field-${i}-option-${j}-code`} />
            <input value={o.label} onChange={(e) => setOpt(j, { label: e.target.value })} className="flex-1 min-w-0 px-2 py-1 text-xs border border-zinc-200" data-testid={`inst-field-${i}-option-${j}-label`} />
            <button onClick={() => move(j, -1)} className="text-zinc-400 hover:text-[#0F172A]" title="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button onClick={() => move(j, 1)} className="text-zinc-400 hover:text-[#0F172A]" title="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
            <button onClick={() => onChange({ options: fld.options.filter((_, k) => k !== j) })} className="text-zinc-400 hover:text-[#EF4444]" title="Remove"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" data-testid={`inst-field-option-code-input-${i}`} className="w-20 px-2 py-1 text-xs font-mono border border-dashed border-zinc-300 outline-none focus:border-[#D4AF37]" />
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOpt(); } }}
            placeholder="+ add choice description, press Enter" data-testid={`inst-field-option-input-${i}`} className="flex-1 min-w-0 px-2 py-1 text-xs border border-dashed border-zinc-300 outline-none focus:border-[#D4AF37]" />
        </div>
      </div>
    </div>
  );
}
